import type { ProductOverride, RulesSaveResult } from '../screens/ProducePriceRules/ProducePriceRules.web';

type ProductKind = ProductOverride['kind'];

export type RuleError = Extract<RulesSaveResult, { saved: false }>['errors'][number];

export const LOOSE_FIXED_PRICE_MESSAGE = 'loose fixed price does not end in .49 or .99';
export const CASE_FIXED_PRICE_MESSAGE = 'case fixed price does not end in .99';
export const LOOSE_CEILING_MESSAGE = 'loose ceiling is 0.49 or less';
export const CASE_CEILING_MESSAGE = 'case ceiling is 0.99 or less';
export const FLOOR_NOT_LOWER_MESSAGE = 'both floor and ceiling are filled and the floor is not lower';
export const NO_LEGAL_PRICE_MESSAGE = 'both floor and ceiling are filled and no legal price fits';

const CENTS_PER_DOLLAR = 100;

const LEGAL_CENT_ENDINGS_BY_KIND: Record<ProductKind, number[]> = {
  loose: [49, 99],
  case: [99],
};

const LOWEST_ILLEGAL_CEILING_CENTS_BY_KIND: Record<ProductKind, number> = {
  loose: 49,
  case: 99,
};

const FIXED_PRICE_MESSAGE_BY_KIND: Record<ProductKind, string> = {
  loose: LOOSE_FIXED_PRICE_MESSAGE,
  case: CASE_FIXED_PRICE_MESSAGE,
};

const CEILING_MESSAGE_BY_KIND: Record<ProductKind, string> = {
  loose: LOOSE_CEILING_MESSAGE,
  case: CASE_CEILING_MESSAGE,
};

const toCents = (value: number | null): number | null => (value === null ? null : Math.round(value * CENTS_PER_DOLLAR));

const centEnding = (cents: number): number => ((cents % CENTS_PER_DOLLAR) + CENTS_PER_DOLLAR) % CENTS_PER_DOLLAR;

const isLegalPrice = (cents: number, kind: ProductKind): boolean => LEGAL_CENT_ENDINGS_BY_KIND[kind].includes(centEnding(cents));

const lowestLegalPriceFrom = (floorCents: number, kind: ProductKind): number => {
  const wholeDollarCents = floorCents - centEnding(floorCents);
  return Math.min(...LEGAL_CENT_ENDINGS_BY_KIND[kind].map((ending) => {
    const price = wholeDollarCents + ending;
    return price >= floorCents ? price : price + CENTS_PER_DOLLAR;
  }));
};

const productPriceErrors = (product: ProductOverride): RuleError[] => {
  const { kind, productNumber } = product;
  const fixedPrice = toCents(product.fixedPrice);
  const floor = toCents(product.floor);
  const ceiling = toCents(product.ceiling);
  const errors: RuleError[] = [];
  if (fixedPrice !== null && !isLegalPrice(fixedPrice, kind)) {
    errors.push({ productNumber, field: 'fixedPrice', message: FIXED_PRICE_MESSAGE_BY_KIND[kind] });
  }
  if (ceiling !== null && ceiling <= LOWEST_ILLEGAL_CEILING_CENTS_BY_KIND[kind]) {
    errors.push({ productNumber, field: 'ceiling', message: CEILING_MESSAGE_BY_KIND[kind] });
  }
  if (floor !== null && ceiling !== null) {
    if (floor >= ceiling) {
      errors.push({ productNumber, field: 'floor', message: FLOOR_NOT_LOWER_MESSAGE });
    } else if (lowestLegalPriceFrom(floor, kind) > ceiling) {
      errors.push({ productNumber, field: 'floor', message: NO_LEGAL_PRICE_MESSAGE });
    }
  }
  return errors;
};

export const productRuleErrors = (products: ProductOverride[]): RuleError[] => products
  .reduce<RuleError[]>((errors, product) => errors.concat(productPriceErrors(product)), []);
